import type { Store, StoreStatus, ShiftDay } from '@/types'

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const

const CLOSING_SOON_THRESHOLD_SECONDS = 30 * 60
const OPENS_SOON_THRESHOLD_SECONDS = 30 * 60

export interface PickupStatus {
  status: StoreStatus
  closingTime: string | null
  nextOpenTime: string | null
  isAvailableForPickup: boolean
}

export interface DeliveryStatus {
  status: StoreStatus
  closingTime: string | null
  nextOpenTime: string | null
  isAvailableForDelivery: boolean
}

function getCurrentDayAndSeconds(timeZone?: string): { dayName: string; currentSeconds: number } {
  const now = new Date()
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timeZone || undefined,
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }
  const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(now)

  const weekday = parts.find(p => p.type === 'weekday')?.value?.toLowerCase() ?? ''
  const hour = parseInt(parts.find(p => p.type === 'hour')?.value ?? '0', 10)
  const minute = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10)
  const second = parseInt(parts.find(p => p.type === 'second')?.value ?? '0', 10)

  return {
    dayName: weekday,
    currentSeconds: hour * 3600 + minute * 60 + second,
  }
}

function getNextDayName(dayName: string): string {
  const idx = DAY_NAMES.indexOf(dayName as typeof DAY_NAMES[number])
  return DAY_NAMES[(idx + 1) % 7]
}

/**
 * Determines the pickup status of a store based on its shifts and timezone.
 */
export function getStorePickupStatus(store: Store): PickupStatus {
  const { dayName, currentSeconds } = getCurrentDayAndSeconds(store.timeZone)
  const todayShift = store.shifts?.[dayName as keyof typeof store.shifts] as ShiftDay | undefined

  if (!todayShift || todayShift.noPickup) {
    const nextDay = getNextDayName(dayName)
    const tomorrowShift = store.shifts?.[nextDay as keyof typeof store.shifts] as ShiftDay | undefined
    const nextOpen = tomorrowShift && !tomorrowShift.noPickup
      ? `Opens ${nextDay} at ${tomorrowShift.restaurantStartDisplay}`
      : null

    return {
      status: 'closed',
      closingTime: null,
      nextOpenTime: nextOpen,
      isAvailableForPickup: false,
    }
  }

  if (todayShift.isOrderingPaused) {
    return {
      status: 'closed',
      closingTime: null,
      nextOpenTime: 'Ordering is paused',
      isAvailableForPickup: false,
    }
  }

  if (todayShift.pickup24 || todayShift.restaurant24) {
    return {
      status: 'open',
      closingTime: null,
      nextOpenTime: null,
      isAvailableForPickup: true,
    }
  }

  const openAt = todayShift.restaurantStart
  const closeAt = todayShift.restaurantEnd

  if (currentSeconds < openAt) {
    const diff = openAt - currentSeconds
    if (diff <= OPENS_SOON_THRESHOLD_SECONDS) {
      return {
        status: 'opens_soon',
        closingTime: todayShift.restaurantEndDisplay,
        nextOpenTime: `Opens at ${todayShift.restaurantStartDisplay}`,
        isAvailableForPickup: false,
      }
    }
    return {
      status: 'closed',
      closingTime: null,
      nextOpenTime: `Opens at ${todayShift.restaurantStartDisplay}`,
      isAvailableForPickup: false,
    }
  }

  if (currentSeconds > closeAt) {
    const nextDay = getNextDayName(dayName)
    const tomorrowShift = store.shifts?.[nextDay as keyof typeof store.shifts] as ShiftDay | undefined
    const nextOpen = tomorrowShift && !tomorrowShift.noPickup
      ? `Opens ${nextDay} at ${tomorrowShift.restaurantStartDisplay}`
      : null

    return {
      status: 'closed',
      closingTime: null,
      nextOpenTime: nextOpen,
      isAvailableForPickup: false,
    }
  }

  const remaining = closeAt - currentSeconds
  if (remaining <= CLOSING_SOON_THRESHOLD_SECONDS) {
    return {
      status: 'closing_soon',
      closingTime: todayShift.restaurantEndDisplay,
      nextOpenTime: null,
      isAvailableForPickup: true,
    }
  }

  return {
    status: 'open',
    closingTime: todayShift.restaurantEndDisplay,
    nextOpenTime: null,
    isAvailableForPickup: true,
  }
}

/**
 * Determines the delivery status of a store based on its delivery shift fields.
 * This calculation is done purely client-side using the store's timezone and the
 * deliveryStart/deliveryEnd fields, ignoring the isDeliveryOpen flag.
 */
export function getStoreDeliveryStatus(store: Store): DeliveryStatus {
  const { dayName, currentSeconds } = getCurrentDayAndSeconds(store.timeZone)
  const todayShift = store.shifts?.[dayName as keyof typeof store.shifts] as ShiftDay | undefined

  if (!todayShift || todayShift.noDelivery) {
    const nextDay = getNextDayName(dayName)
    const tomorrowShift = store.shifts?.[nextDay as keyof typeof store.shifts] as ShiftDay | undefined
    const nextOpen = tomorrowShift && !tomorrowShift.noDelivery && tomorrowShift.deliveryStartDisplay
      ? `Opens ${nextDay} at ${tomorrowShift.deliveryStartDisplay}`
      : null

    return {
      status: 'closed',
      closingTime: null,
      nextOpenTime: nextOpen,
      isAvailableForDelivery: false,
    }
  }

  if (todayShift.isOrderingPaused) {
    return {
      status: 'closed',
      closingTime: null,
      nextOpenTime: 'Ordering is paused',
      isAvailableForDelivery: false,
    }
  }

  if (todayShift.delivery24) {
    return {
      status: 'open',
      closingTime: null,
      nextOpenTime: null,
      isAvailableForDelivery: true,
    }
  }

  const openAt = todayShift.deliveryStart
  const closeAt = todayShift.deliveryEnd

  if (currentSeconds < openAt) {
    const diff = openAt - currentSeconds
    if (diff <= OPENS_SOON_THRESHOLD_SECONDS) {
      return {
        status: 'opens_soon',
        closingTime: null,
        nextOpenTime: todayShift.deliveryStartDisplay ? `Opens at ${todayShift.deliveryStartDisplay}` : null,
        isAvailableForDelivery: false,
      }
    }
    return {
      status: 'closed',
      closingTime: null,
      nextOpenTime: todayShift.deliveryStartDisplay ? `Opens at ${todayShift.deliveryStartDisplay}` : null,
      isAvailableForDelivery: false,
    }
  }

  if (currentSeconds > closeAt) {
    const nextDay = getNextDayName(dayName)
    const tomorrowShift = store.shifts?.[nextDay as keyof typeof store.shifts] as ShiftDay | undefined
    const nextOpen = tomorrowShift && !tomorrowShift.noDelivery && tomorrowShift.deliveryStartDisplay
      ? `Opens ${nextDay} at ${tomorrowShift.deliveryStartDisplay}`
      : null

    return {
      status: 'closed',
      closingTime: null,
      nextOpenTime: nextOpen,
      isAvailableForDelivery: false,
    }
  }

  const remaining = closeAt - currentSeconds
  if (remaining <= CLOSING_SOON_THRESHOLD_SECONDS) {
    return {
      status: 'closing_soon',
      closingTime: todayShift.deliveryEndDisplay || null,
      nextOpenTime: null,
      isAvailableForDelivery: true,
    }
  }

  return {
    status: 'open',
    closingTime: todayShift.deliveryEndDisplay || null,
    nextOpenTime: null,
    isAvailableForDelivery: true,
  }
}

/**
 * Filters stores to only those that offer pickup (noPickup !== true for today).
 */
export function filterStoresForPickup(stores: Store[]): Store[] {
  return stores.filter(store => {
    const { dayName } = getCurrentDayAndSeconds(store.timeZone)
    const todayShift = store.shifts?.[dayName as keyof typeof store.shifts] as ShiftDay | undefined
    return !todayShift?.noPickup
  })
}

/**
 * Checks whether a store offers delivery today based on its current shift.
 */
export function storeOffersDeliveryToday(store: Store): boolean {
  const { dayName } = getCurrentDayAndSeconds(store.timeZone)
  const todayShift = store.shifts?.[dayName as keyof typeof store.shifts] as ShiftDay | undefined
  if (!todayShift) return false
  return !todayShift.noDelivery
}
